import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement, ArcElement } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase-config';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement, ArcElement);

export default function AIAnalyticsDashboard() {
  const [analyticsData, setAnalyticsData] = useState({
    conversionRates: [],
    userBehavior: [],
    campaignPerformance: [],
    predictions: {},
    insights: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Simulate AI-powered analytics data
      const mockData = {
        conversionRates: {
          labels: ['Hét 1', 'Hét 2', 'Hét 3', 'Hét 4'],
          datasets: [{
            label: 'Konverziós arány (%)',
            data: [12.5, 15.2, 18.7, 22.1],
            borderColor: '#e31937',
            backgroundColor: 'rgba(227, 25, 55, 0.1)',
            tension: 0.4
          }]
        },
        userBehavior: {
          labels: ['Mobilház', 'Ingatlan', 'Autó', 'Egyéb'],
          datasets: [{
            label: 'Érdeklődések',
            data: [45, 30, 20, 5],
            backgroundColor: ['#e31937', '#ff6b6b', '#4ecdc4', '#45b7d1']
          }]
        },
        campaignPerformance: {
          labels: ['Facebook', 'Google Ads', 'Email', 'Organikus'],
          datasets: [{
            label: 'Lead generálás',
            data: [85, 120, 65, 95],
            backgroundColor: '#e31937'
          }]
        },
        predictions: {
          nextMonthLeads: 340,
          expectedRevenue: 2850000,
          conversionTrend: 'növekvő',
          bestPerformingSegment: 'Mobilház vásárlók'
        },
        insights: [
          {
            type: 'success',
            title: 'Kiváló teljesítmény',
            description: 'A mobilház kategóriában 25%-kal nőtt az érdeklődés az elmúlt héten.',
            confidence: 95
          },
          {
            type: 'warning',
            title: 'Figyelemre méltó trend',
            description: 'Az autó kategóriában csökkenő tendencia figyelhető meg.',
            confidence: 78
          },
          {
            type: 'info',
            title: 'AI Javaslat',
            description: 'Javasoljuk a Facebook hirdetések költségvetésének 15%-kal történő növelését.',
            confidence: 88
          }
        ]
      };

      setAnalyticsData(mockData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
    setLoading(false);
  };

  const generateAIInsight = () => {
    const insights = [
      "Az AI elemzés szerint a hétvégi kampányok 40%-kal hatékonyabbak.",
      "A mobilház érdeklődők 65%-a 25-45 év közötti korosztályból származik.",
      "A személyre szabott email kampányok 3x jobb konverziót érnek el.",
      "A videós hirdetések 2.5x több interakciót generálnak."
    ];
    
    return insights[Math.floor(Math.random() * insights.length)];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e31937]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          AI Analitika & Előrejelzések
        </h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
        >
          <option value="7d">Utolsó 7 nap</option>
          <option value="30d">Utolsó 30 nap</option>
          <option value="90d">Utolsó 90 nap</option>
        </select>
      </div>

      {/* AI Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {analyticsData.insights.map((insight, index) => (
          <div key={index} className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
            <div className="flex items-start space-x-3">
              <div className={`w-3 h-3 rounded-full mt-1 ${
                insight.type === 'success' ? 'bg-green-500' :
                insight.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
              }`}></div>
              <div className="flex-1">
                <h4 className="text-white font-semibold text-sm">{insight.title}</h4>
                <p className="text-gray-400 text-xs mt-1">{insight.description}</p>
                <div className="flex items-center mt-2">
                  <span className="text-xs text-gray-500">AI Bizonyosság:</span>
                  <div className="ml-2 flex-1 bg-gray-700 rounded-full h-1">
                    <div 
                      className="bg-[#e31937] h-1 rounded-full" 
                      style={{width: `${insight.confidence}%`}}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2">{insight.confidence}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Predictions Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Jövő havi lead-ek</p>
              <p className="text-white text-2xl font-bold">{analyticsData.predictions.nextMonthLeads}</p>
              <p className="text-green-400 text-xs">+15% előrejelzés</p>
            </div>
            <div className="w-12 h-12 bg-[#e31937]/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Várható bevétel</p>
              <p className="text-white text-xl font-bold">{(analyticsData.predictions.expectedRevenue / 1000000).toFixed(1)}M Ft</p>
              <p className="text-green-400 text-xs">+8% növekedés</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Konverziós trend</p>
              <p className="text-white text-xl font-bold capitalize">{analyticsData.predictions.conversionTrend}</p>
              <p className="text-blue-400 text-xs">AI elemzés</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Top szegmens</p>
              <p className="text-white text-sm font-bold">{analyticsData.predictions.bestPerformingSegment}</p>
              <p className="text-purple-400 text-xs">Legjobb ROI</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Rate Trend */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white font-semibold mb-4">Konverziós Arány Trend</h3>
          <Line 
            data={analyticsData.conversionRates}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  labels: { color: 'white' }
                }
              },
              scales: {
                x: {
                  ticks: { color: 'white' },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                },
                y: {
                  ticks: { color: 'white' },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                }
              }
            }}
          />
        </div>

        {/* User Behavior */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white font-semibold mb-4">Felhasználói Viselkedés</h3>
          <Doughnut 
            data={analyticsData.userBehavior}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  labels: { color: 'white' }
                }
              }
            }}
          />
        </div>

        {/* Campaign Performance */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white font-semibold mb-4">Kampány Teljesítmény</h3>
          <Bar 
            data={analyticsData.campaignPerformance}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  labels: { color: 'white' }
                }
              },
              scales: {
                x: {
                  ticks: { color: 'white' },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                },
                y: {
                  ticks: { color: 'white' },
                  grid: { color: 'rgba(255,255,255,0.1)' }
                }
              }
            }}
          />
        </div>

        {/* AI Assistant */}
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white font-semibold mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            AI Asszisztens Javaslatok
          </h3>
          <div className="space-y-3">
            <div className="bg-[#2a2a2a] rounded-lg p-3">
              <p className="text-white text-sm">{generateAIInsight()}</p>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-[#e31937] hover:bg-[#b91c1c] text-white py-2 px-4 rounded-lg text-sm transition-colors"
            >
              Új AI Javaslat Generálása
            </button>
          </div>
        </div>
      </div>

      {/* Real-time Metrics */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <h3 className="text-white font-semibold mb-4">Valós Idejű Metrikák</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#e31937]">127</p>
            <p className="text-gray-400 text-sm">Mai látogatók</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-500">23</p>
            <p className="text-gray-400 text-sm">Aktív lead-ek</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-500">8.5%</p>
            <p className="text-gray-400 text-sm">Konverzió ma</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-500">€2,340</p>
            <p className="text-gray-400 text-sm">Mai bevétel</p>
          </div>
        </div>
      </div>
    </div>
  );
}
